/**
 * Shared log line renderer for displaying logs with ANSI colors, timestamps, and labels
 * Includes search highlighting and W&B run ID linking by default
 */

import {Alias} from '@wandb/weave/components/Tag';
import {AnsiUp} from 'ansi_up';
import React from 'react';

import {DateFormat, format, isValidDate} from '../../../util/date';
import {
  MAX_VISIBLE_LINE_LENGTH,
  REGEX_RUN_ID,
  TRUNCATED_LABEL_MAX_CHARS,
} from './constants';
import {LogLabel} from './LogLabel';

export interface LogLineData {
  id: string;
  line: string;
  level?: string;
  label?: string;
  timestamp: string;
  lineNum?: number;
}

/**
 * Search match information for highlighting
 */
export type SearchMatch = {
  row: number;
  col: number;
};

export interface SearchMatches {
  indexed: {[row: number]: number[]};
  ordered: SearchMatch[];
}

export interface LogLineRendererProps {
  line: LogLineData;
  /** Row index for search highlighting */
  index: number;
  showTimestamp?: boolean;
  showLabel?: boolean;
  showLineNumber?: boolean;
  maxLineNumLength?: number;
  /** Maximum label length for alignment (0 means no labels) */
  maxLabelLength?: number;
  style?: React.CSSProperties;
  className?: string;
  /** Search text for highlighting */
  searchText?: string;
  /** Search match locations */
  searchMatches?: SearchMatches;
  /** Currently active search match index */
  searchActive?: number;
}

// ANSI escape codes for search highlighting
const ANSI_BACKGROUND_YELLOW = '\u001b[48;5;11m';
const ANSI_BACKGROUND_ORANGE = '\u001b[48;5;220m';
const ANSI_RESET = '\u001b[0m';

/**
 * Insert wrapper text at each location
 */
const wrapRanges = (
  text: string,
  indices: number[],
  length: number,
  start: (index: number) => string,
  end: (index: number) => string
) => {
  let result = '';
  let lastIndex = 0;
  for (const index of indices) {
    result += text.substring(lastIndex, index);
    const startText = start(index);
    const endText = end(index);
    result += startText + text.substring(index, index + length) + endText;
    lastIndex = index + length;
  }
  result += text.substring(lastIndex);
  return result;
};

/**
 * Convert raw log text to HTML with search highlighting and run ID linking
 */
const convertToHtml = (
  index: number,
  text: string,
  searchText: string,
  searchMatches: SearchMatches,
  searchActive: number
): string => {
  // Create new instance of ansi_up for each line to avoid unterminated escape codes
  const ansiUp = new AnsiUp();
  const lineMatches = searchMatches.indexed[index] ?? [];
  let activeIndex = -1;

  if (searchActive !== -1) {
    const active = searchMatches.ordered[searchActive];
    if (active.row === index) {
      activeIndex = active.col;
    }
  }

  // Add search highlighting using ANSI codes
  const highlighted = wrapRanges(
    text,
    lineMatches,
    searchText.length,
    idx =>
      idx === activeIndex ? ANSI_BACKGROUND_ORANGE : ANSI_BACKGROUND_YELLOW,
    () => ANSI_RESET
  );

  // Check for W&B run IDs and create links
  const match = REGEX_RUN_ID.exec(highlighted);
  if (match == null) {
    return ansiUp.ansi_to_html(highlighted.replace(/ /g, '\u00a0'));
  }

  const [, projectName, entityName, name] = match;
  const urlRun = `/${entityName}/${projectName}/runs/${name}`;
  const link = `<a href="${urlRun}">${name}</a>`;
  const parts = highlighted
    .split(name)
    .map(part => ansiUp.ansi_to_html(part.replace(/ /g, '\u00a0')));
  return parts.join(link);
};

/**
 * Renders a single log line with optional timestamp, label, and line number
 */
export const LogLineRenderer: React.FC<LogLineRendererProps> = ({
  line,
  index,
  showTimestamp = true,
  showLabel = true,
  showLineNumber = false,
  maxLineNumLength = 0,
  maxLabelLength = 0,
  style,
  className = '',
  searchText = '',
  searchMatches = {indexed: {}, ordered: []},
  searchActive = -1,
}) => {
  // Add 'Z' to timestamp if it doesn't have one to ensure it's parsed as UTC
  const timestampStr = line.timestamp.endsWith('Z')
    ? line.timestamp
    : `${line.timestamp}Z`;
  const timestamp = new Date(timestampStr);

  // Truncate extremely long lines for performance
  const rawText =
    line.line.length > MAX_VISIBLE_LINE_LENGTH
      ? line.line.substring(0, MAX_VISIBLE_LINE_LENGTH)
      : line.line;

  // Convert to HTML with search highlighting and run ID linking
  const htmlContent =
    convertToHtml(index, rawText, searchText, searchMatches, searchActive) +
    '<br />';

  return (
    <div role="row" style={style} className={className}>
      <div className="flex gap-8">
        {showLineNumber && line.lineNum != null && (
          <span aria-label="line number" className="select-none text-moon-500">
            {`${line.lineNum}`.padStart(maxLineNumLength, '\u00a0')}
          </span>
        )}
        <div className="flex flex-wrap gap-8 md:flex-nowrap">
          {showTimestamp && isValidDate(timestamp) && (
            <span data-testid="timestamp-alias" className="shrink-0">
              <Alias
                color="moon"
                label={format(timestamp, DateFormat.YYYY_MM_DD_HH_MM_SS_DASHES)}
              />
            </span>
          )}
          {showLabel && maxLabelLength > 0 && (
            <LogLabel
              label={line.label}
              maxLabelLength={maxLabelLength}
              truncatedMaxChars={TRUNCATED_LABEL_MAX_CHARS}
            />
          )}
          <span
            className="break-all dark:text-moon-800"
            dangerouslySetInnerHTML={{
              __html:
                // The ansi to HTML library takes care of escaping.
                // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                htmlContent,
            }}
          />
        </div>
      </div>
    </div>
  );
};
